import { Plus } from '@menuzen/icon/build/outline';
import { useEditor } from '@mnz/builder';
import { Box } from '@mui/material';
import classNames from 'classnames';
import React, { useLayoutEffect, useState } from 'react';
import { DragDropContext, Droppable, DropResult, Draggable } from 'react-beautiful-dnd';

import Dialog, { DialogProps } from '../../../../components/dialog/Dialog';
import EditIcon from '../../../../components/icon/EditIcon';
import { useDragAndDrop } from '../../../../hooks/useDragAndDrop';
import { reorder } from '../../../../shared/utils/order';
import { BLOCKS_MODE, MODAL } from '../config';
import BlockCard from './BlockCard';
import DeleteBlockModal from './DeleteBlockModal';

interface BlockModalProps extends DialogProps {
    setModal: (newModal: string | null) => void;
}
const BlockModal: React.FC<BlockModalProps> = ({ setModal, onClose, ...props }) => {
    const {
        actions: { delete: deleteNode, move: moveNode },
        requiredNodes,
        noRequiredNodes,
    } = useEditor((_, query) => {
        const nodes = query.getNodes();
        const requiredNodes: string[] = [];
        const noRequiredNodes: string[] = [];
        if (nodes.ROOT) {
            nodes.ROOT.data.nodes.forEach((node: string) => {
                if (nodes[node].data.name === 'BasicInfo' || nodes[node].data.name === 'Social') {
                    requiredNodes.push(node);
                } else {
                    noRequiredNodes.push(node);
                }
            });
        }
        return {
            nodes,
            requiredNodes,
            noRequiredNodes,
        };
    });

    const { dragCss, placeholderCss, onDragEnd: handleOnDragEnd, onDragUpdate } = useDragAndDrop();
    const [openDeleteBlockModal, setOpenDeleteBlockModal] = useState(false);
    const [mode, setMode] = useState<string>('');
    const [selectedNode, setSelectedNode] = useState('');
    const [showWarning, setShowWarning] = useState(true);
    const [noRequiredNodesClone, setNoRequiredNodesClone] = useState<string[]>(noRequiredNodes);

    useLayoutEffect(() => {
        setNoRequiredNodesClone(noRequiredNodes);
    }, [JSON.stringify(noRequiredNodes)]);

    const deleteBlock = (id: string) => {
        if (!showWarning) {
            deleteNode(id);
        } else {
            setSelectedNode(id);
            setOpenDeleteBlockModal(true);
        }
    };

    const onCloseModal = (e: React.SyntheticEvent) => {
        onClose(e);
        setMode('');
    };

    const onDragEnd = (res: DropResult) => {
        if (!res.destination) {
            return;
        }
        const destination = res.destination.index;
        const source = res.source.index;
        handleOnDragEnd();
        setNoRequiredNodesClone(reorder(noRequiredNodesClone, source, destination));
        moveNode(
            res.draggableId,
            'ROOT',
            destination - source < 1 ? destination + requiredNodes.length : destination + requiredNodes.length + 1,
        );
    };

    return (
        <>
            <Dialog onClose={onCloseModal} {...props}>
                <div className="mt-8 px-24 pb-50">
                    <div className="flex items-center justify-center relative h-32 mb-12">
                        <p className="text-18 font-semibold">Blocks</p>
                        <button
                            onClick={() => {
                                if (mode === BLOCKS_MODE.EDIT) {
                                    setMode('');
                                } else {
                                    setMode(BLOCKS_MODE.EDIT);
                                }
                            }}
                            className="h-full flex items-center justify-center py-8 px-14 rounded-round bg-primary-10 absolute right-0 top-0"
                        >
                            {mode === BLOCKS_MODE.EDIT ? (
                                <p className="ml-4 text-14">Done</p>
                            ) : (
                                <>
                                    <div className="text-16">
                                        <EditIcon />
                                    </div>
                                    <p className="ml-4 text-14">Edit</p>
                                </>
                            )}
                        </button>
                    </div>
                    <div
                        className={classNames([
                            'px-10 py-6 rounded-10 bg-primary-10 flex flex-col my-12',
                            {
                                'opacity-50': mode === BLOCKS_MODE.EDIT,
                            },
                        ])}
                    >
                        {requiredNodes.map((node) => (
                            <BlockCard key={node} nodeId={node} mode={mode} isRequiredBlock />
                        ))}
                    </div>
                    <div className="px-10 py-6 rounded-10 bg-primary-10 flex flex-col my-12">
                        <DragDropContext onDragEnd={(res) => onDragEnd(res)} onDragUpdate={onDragUpdate}>
                            <Droppable droppableId="orderLink-dropable">
                                {(provided) => {
                                    return (
                                        <div
                                            className="flex flex-col relative"
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                        >
                                            {noRequiredNodesClone.map((node, i) => (
                                                <Draggable key={node} index={i} draggableId={node}>
                                                    {(provided, snapshot) => (
                                                        <BlockCard
                                                            nodeId={node}
                                                            mode={mode}
                                                            onDelete={() => deleteBlock(node)}
                                                            provided={provided}
                                                            dragCss={snapshot.isDragging ? dragCss : {}}
                                                        />
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                            <Box
                                                sx={{
                                                    ...placeholderCss,
                                                    borderRadius: '8px',
                                                }}
                                            />
                                        </div>
                                    );
                                }}
                            </Droppable>
                        </DragDropContext>
                        {mode !== BLOCKS_MODE.EDIT && (
                            <button
                                className="flex items-center justify-start px-12 py-11 text-white text-16 rounded-6 bg-teal my-4"
                                onClick={() => setModal(MODAL.ADD_BLOCK)}
                            >
                                <div className="mr-6">
                                    <Plus />
                                </div>
                                <p className="font-semibold leading-none">Add Block</p>
                            </button>
                        )}
                    </div>
                </div>
            </Dialog>
            <DeleteBlockModal
                title="Delete Block"
                showClose
                open={openDeleteBlockModal}
                onClose={() => {
                    setSelectedNode('');
                    setOpenDeleteBlockModal(false);
                }}
                showWarning={showWarning}
                setShowWarning={setShowWarning}
                onDelete={() => {
                    deleteNode(selectedNode);
                    setSelectedNode('');
                    setOpenDeleteBlockModal(false);
                }}
            />
        </>
    );
};

export default BlockModal;
